import React, { useContext, useEffect, useRef } from 'react';
import { PostEditorEditView, PostEditorEditViewProps } from './PostEditorEdit.view';
import {
  OnSubmitErrorPostEditorFunction,
  OnSubmitPostEditorFunction
} from '../PostEditorModal/PostEditorModal.controller';
import { SubmitHandler } from 'react-hook-form/dist/types/form';
import {
  PostEditorContextEnum,
  PostEditorSubmitData,
  PostEditorSubmitErrorPayload,
  PostEditorSubmitPayload
} from '../../../models/post-editor';
import { showToast } from '../../../helpers/showToast';
import { SubmitErrorHandler, useForm, UseFormReturn } from 'react-hook-form';
import {
  PlanUsageType,
  UsageFieldsFragmentDoc,
  useBulkUpdatePostsMutation,
  useCreatePostMutation
} from '../../../models/graphql';
import { useSelectedOrganization } from '../../../hooks/useSelectedOrganization';
import { AppContext } from '../../../providers/AppProvider';
import { usePostEditorPostFetch } from '../../../hooks/usePostEditorPostFetch';
import { useSettings } from '../../../hooks/useSettings';
import { FormOptions } from '../../../constants/form';
import { setProfileSelectorFormValues } from '../../../components/socialProfiles/SocialProfileSelector/SocialProfileSelector.view';
import { useClosePostEditor } from '../../../hooks/useClosePostEditor';
import { useApolloClient } from '@apollo/client';
import { mapStateToPostInput } from '../helpers/mapStateToPostInput';
import { useOrganizationPlan } from 'hooks/useOrganizationPlan';

export type PostEditorEditControllerProps = {
  postId: PostEditorEditViewProps['postId'];
};

export const PostEditorEditController = ({ postId }: PostEditorEditControllerProps): JSX.Element => {
  const formInstance: UseFormReturn = useForm({
    ...FormOptions
  });
  const { loading, initialState, profiles, categories, error, fetchedFamily } = usePostEditorPostFetch(postId, 'edit');
  const createPostHookResult = useCreatePostMutation();
  const updatePostsHookResult = useBulkUpdatePostsMutation();
  const { selectedWorkspace } = useSelectedOrganization();
  const { selectedWorkspaceSettings } = useSettings();
  const { cache } = useApolloClient();
  const ref = useRef<HTMLDivElement | null>(null);

  // Set form state for the profiles after loading.
  useEffect(() => {
    if (!loading && profiles && initialState) {
      const activeProfiles = initialState.postGroups[0].profiles;
      setProfileSelectorFormValues(profiles, activeProfiles, formInstance, 'postGroup_0_profiles');
    }
  }, [formInstance, initialState, loading, profiles]);
  const { remainingPosts, getUsageFieldsLazyQuery } = useOrganizationPlan();
  const { postEditorContext, setLimitReachedModalState } = useContext(AppContext);
  const { closePostEditor } = useClosePostEditor();
  const onClosePostEditor =
    postEditorContext.type !== PostEditorContextEnum.undefined ? postEditorContext.onClose : undefined;
  const submitted = useRef<boolean>(false);

  const updateCache = (qtyNewPosts: number) => {
    if (qtyNewPosts <= 0) {
      return;
    }
    const oldUsage: PlanUsageType | null = cache.readFragment({
      fragment: UsageFieldsFragmentDoc,
      fragmentName: 'UsageFields',
      id: 'PlanUsageType:{}'
    });
    const newUsage = { ...oldUsage, posts: (oldUsage?.posts ?? 0) + qtyNewPosts };
    cache.writeFragment({
      fragment: UsageFieldsFragmentDoc,
      fragmentName: 'UsageFields',
      id: 'PlanUsageType:{}',
      data: newUsage
    });
  };

  const onSubmitHandler: OnSubmitPostEditorFunction<SubmitHandler<PostEditorSubmitData>> =
    (dispatch, profiles, state) => async data => {
      try {
        if (!selectedWorkspace || profiles === undefined || !selectedWorkspaceSettings) {
          throw new Error('No Selected Workspace or selected profiles are undefined.');
        }
        const defaultQueuePosition = selectedWorkspaceSettings.defaultQueuePosition;
        const payload: PostEditorSubmitPayload = {
          ref,
          submitted,
          profiles,
          workspaceId: selectedWorkspace.id,
          data,
          type: 'single',
          context: 'update',
          callback: success => {
            onClosePostEditor && onClosePostEditor(success);
            closePostEditor();
            if (success) {
              const { createPosts } = mapStateToPostInput(state, profiles, defaultQueuePosition, fetchedFamily);
              const qtyNewPosts = createPosts ? createPosts[0].posts.length : 0;
              updateCache(qtyNewPosts);
            }
          },
          defaultQueuePosition,
          createPostHookResult,
          updatePostsHookResult,
          // this should be part of the callback
          onPlanLimitException: () => {
            setLimitReachedModalState({
              type: 'posts',
              isOpen: true,
              remaining: remainingPosts > 0 ? remainingPosts : undefined
            });
            submitted.current = false;
          },
          fetchedFamily
        };
        // We want to update the values of usage in case we hit a limit
        getUsageFieldsLazyQuery();
        dispatch({ type: 'submitEditor', payload });
      } catch (e) {
        showToast({
          summary: 'Submit Error',
          detail: (e as Error).message,
          severity: 'error'
        });
      }
    };

  const onErrorHandler: OnSubmitErrorPostEditorFunction<SubmitErrorHandler<PostEditorSubmitData>> = dispatch => () => {
    const payload: PostEditorSubmitErrorPayload = {
      type: 'single'
    };
    submitted.current = false;
    dispatch({ type: 'submitErrorEditor', payload });
  };

  useEffect(() => {
    if (error) {
      onClosePostEditor && onClosePostEditor(false);
      closePostEditor();
    }
  }, [error, closePostEditor, onClosePostEditor]);

  const viewProps: PostEditorEditViewProps = {
    postId,
    loading,
    initialState,
    onErrorHandler,
    onSubmitHandler,
    categories,
    profiles,
    formInstance
  };

  return <PostEditorEditView ref={ref} {...viewProps} />;
};
